import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { Product } from '../../types';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useTranslation } from '../../lib/i18n';
interface ProductCardProps {
  product: Product;
  layout?: 'vertical' | 'horizontal';
  onRequestQuote?: (productId: string) => void;
}
export function ProductCard({
  product,
  layout = 'vertical',
  onRequestQuote
}: ProductCardProps) {
  const {
    t,
    lang
  } = useTranslation();
  const soldOut = product.soldOut === true;
  const comingSoon = product.comingSoon === true;
  const disabled = soldOut || comingSoon;
  const badgeText = soldOut ? 'Prodato' : comingSoon ? 'Uskoro dostupno' : '';
  if (layout === 'horizontal') {
    return <Card hover className={`flex flex-col md:flex-row gap-4 md:gap-6 ${disabled ? 'opacity-70' : ''}`} data-id="element-339">
        <div className="w-full md:w-1/3 flex-shrink-0 relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-4 md:p-6 flex items-center justify-center min-h-[200px] md:min-h-0 overflow-hidden" data-id="element-340">
          <img src={product.image} alt={`${product.name} ${product.model}`} className={`w-full h-auto object-contain ${disabled ? 'brightness-40 grayscale' : ''}`} data-id="element-341" />
          {disabled && <div className="absolute inset-0 flex items-center justify-center" data-id="element-342">
              <div className="absolute inset-0 bg-black/50" data-id="element-343" />
              <div className={`relative rotate-[-35deg] ${soldOut ? 'bg-red-600' : 'bg-brand-yellow text-dark-bg'} font-black text-lg tracking-widest uppercase px-8 py-2 shadow-lg`} data-id="element-344">
                {badgeText}
              </div>
            </div>}
        </div>
        <div className="flex-1 flex flex-col" data-id="element-345">
          <div className="mb-3 md:mb-4" data-id="element-346">
            <div className="flex items-center gap-3 mb-1" data-id="element-347">
              <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-348">
                {product.name}
              </h3>
              {disabled && <span className={`text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded ${soldOut ? 'bg-red-600/20 text-red-400 border border-red-600/40' : 'bg-brand-yellow/20 text-brand-yellow border border-brand-yellow/40'}`} data-id="element-349">
                  {badgeText}
                </span>}
            </div>
            <p className="text-base md:text-lg text-gray-400" data-id="element-350">
              {product.model}
            </p>
          </div>
          <p className="text-sm md:text-base text-gray-300 mb-3 md:mb-4" data-id="element-351">
            {product.description}
          </p>
          <div className="mb-3 md:mb-4 space-y-2" data-id="element-352">
            <div className="flex items-start gap-2 p-2 bg-brand-yellow bg-opacity-10 rounded border border-brand-yellow border-opacity-30" data-id="element-353">
              <ShieldCheckIcon className="w-4 h-4 md:w-5 md:h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-354" />
              <div data-id="element-355">
                <p className="text-xs md:text-sm font-semibold text-brand-yellow" data-id="element-356">
                  {t.showcase.safetyTitle}
                </p>
                <p className="text-xs text-gray-300" data-id="element-357">{t.showcase.safetyText}</p>
              </div>
            </div>
            {product.highlights.slice(0, 3).map((highlight, index) => <div key={index} className="flex items-center gap-2" data-id="element-358">
                <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-brand-yellow flex-shrink-0" data-id="element-359" />
                <span className="text-xs md:text-sm text-gray-300" data-id="element-360">
                  {highlight}
                </span>
              </div>)}
          </div>
          <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-4 p-2 md:p-3 bg-dark-bg rounded-industrial" data-id="element-361">
            <div data-id="element-362">
              <p className="text-xs text-gray-500" data-id="element-363">{t.showcase.capacity}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-364">
                {product.specs.capacity}
              </p>
            </div>
            <div data-id="element-365">
              <p className="text-xs text-gray-500" data-id="element-366">{t.showcase.motor}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-367">
                {product.specs.motor}
              </p>
            </div>
            <div data-id="element-368">
              <p className="text-xs text-gray-500" data-id="element-369">{t.showcase.battery}</p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-370">
                {product.specs.battery}
              </p>
            </div>
            <div data-id="element-371">
              <p className="text-xs text-gray-500" data-id="element-372">
                {lang === 'en' ? 'Weight' : 'Težina'}
              </p>
              <p className="text-xs md:text-sm font-semibold text-white" data-id="element-373">
                {product.specs.weight}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3 md:mb-4 italic" data-id="element-374">
            {t.showcase.batteryNote}
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4 mt-auto" data-id="element-375">
            <div data-id="element-376">
              <div className="flex items-baseline gap-2" data-id="element-377">
                <span className={`text-2xl md:text-3xl font-bold ${disabled ? 'text-gray-500 line-through' : 'text-brand-yellow'}`} data-id="element-378">
                  €{product.price.toLocaleString()}
                </span>
                <span className="text-xs text-gray-400" data-id="element-379">bez PDV</span>
              </div>
              {!disabled && <p className="text-xs text-gray-500 mt-0.5" data-id="element-380">
                  Sa PDV: €{Math.round(product.price * 1.2).toLocaleString()} ·
                  Rok isporuke 2 meseca
                </p>}
            </div>
            {onRequestQuote && <Button variant="primary" size="md" onClick={disabled ? undefined : () => onRequestQuote(product.id)} disabled={disabled} className={`w-full sm:w-auto ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`} data-id="element-381">
                {disabled ? badgeText : t.showcase.requestQuote}
                {!disabled && <ArrowRightIcon className="w-4 h-4 ml-2 inline" data-id="element-382" />}
              </Button>}
          </div>
        </div>
      </Card>;
  }
  return <Card hover className={`flex flex-col h-full ${disabled ? 'opacity-70' : ''}`} data-id="element-383">
      <div className="relative w-full h-64 bg-gradient-to-br from-gray-900 to-gray-800 rounded-industrial p-4 mb-4 flex items-center justify-center overflow-hidden" data-id="element-384">
        <img src={product.image} alt={`${product.name} ${product.model}`} className={`w-full h-full object-contain ${disabled ? 'brightness-40 grayscale' : ''}`} data-id="element-385" />
        {disabled && <div className="absolute inset-0 flex items-center justify-center" data-id="element-386">
            <div className="absolute inset-0 bg-black/50" data-id="element-387" />
            <div className={`relative rotate-[-35deg] ${soldOut ? 'bg-red-600 text-white' : 'bg-brand-yellow text-dark-bg'} font-black text-xl tracking-widest uppercase px-10 py-2 shadow-lg`} data-id="element-388">
              {badgeText}
            </div>
          </div>}
      </div>
      <div className="mb-4" data-id="element-389">
        <div className="flex items-center gap-2 mb-1" data-id="element-390">
          <h3 className="text-xl font-bold text-white" data-id="element-391">{product.name}</h3>
          {disabled && <span className={`text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded ${soldOut ? 'bg-red-600/20 text-red-400 border border-red-600/40' : 'bg-brand-yellow/20 text-brand-yellow border border-brand-yellow/40'}`} data-id="element-392">
              {badgeText}
            </span>}
        </div>
        <p className="text-base text-gray-400" data-id="element-393">{product.model}</p>
      </div>
      <p className="text-gray-300 mb-4 flex-1" data-id="element-394">{product.description}</p>
      <div className="mb-4" data-id="element-395">
        <div className="flex items-baseline gap-2" data-id="element-396">
          <span className={`text-2xl font-bold ${disabled ? 'text-gray-500 line-through' : 'text-brand-yellow'}`} data-id="element-397">
            €{product.price.toLocaleString()}
          </span>
          <span className="text-xs text-gray-400" data-id="element-398">bez PDV</span>
        </div>
        {!disabled && <p className="text-xs text-gray-500 mt-0.5" data-id="element-399">
            Sa PDV: €{Math.round(product.price * 1.2).toLocaleString()} · Rok
            isporuke 2 meseca
          </p>}
      </div>
      {onRequestQuote && <Button variant="primary" size="md" className={`w-full ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`} onClick={disabled ? undefined : () => onRequestQuote(product.id)} disabled={disabled} data-id="element-400">
          {disabled ? badgeText : t.showcase.requestQuote}
          {!disabled && <ArrowRightIcon className="w-4 h-4 ml-2 inline" data-id="element-401" />}
        </Button>}
    </Card>;
}